import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import type { CorreoParaIA } from "@/lib/api";
import { IconoAdjunto } from "@/ui/Iconos";
import { ToggleTrack } from "@/ui/Toggle";

/**
 * Elige qué correos del caso lee la IA al redactar.
 *
 * Hasta el 2026-08-18 la IA solo veía las notas manuales y el hilo del ticket
 * del proveedor: los correos del caso le eran invisibles, y ahí es donde viene
 * el dato que hace falta. Joe: *«pueden tener información importante… por
 * ejemplo el correo que llegó del cliente de su proveedor de datos»*.
 *
 * **Arrancan APAGADOS, y es decisión de Joe, no un default por flojera.** Yo
 * propuse lo contrario —medido, un caso trae 1 o 2 correos, así que prenderlos
 * todos costaba ~1,000 tokens y cero clics— y su respuesta le gana a la
 * medición: *«prefiero prenderlos, porque si son muchos correos ahí me vas a
 * ver desmarcando un chingo»*. Yo medí el caso promedio; él pensó en el caso
 * que duele.
 *
 * Lo que sí se hizo para que «apagado» no se convierta en «se me olvidó que
 * existía»:
 * - el encabezado plegado **dice el estado** («ninguno va a la IA»), así que se
 *   ve sin abrir la sección;
 * - hay **Marcar todos** de un clic, que en un caso de 1–2 correos es un toque;
 * - cada renglón trae su adelanto, para elegir sin abrir el correo.
 */

type Props = {
  casoId: number;
  /** Ids marcados. El padre manda esto tal cual al endpoint de la IA. */
  seleccion: number[];
  onCambio: (ids: number[]) => void;
  /** Abre la sección de entrada (en escritorio hay lugar; en móvil estorba). */
  abiertaInicial?: boolean;
  className?: string;
};

export default function CorreosParaIA({
  casoId,
  seleccion,
  onCambio,
  abiertaInicial = false,
  className = "",
}: Props) {
  const [correos, setCorreos] = useState<CorreoParaIA[] | null>(null);
  const [abierta, setAbierta] = useState(abiertaInicial);

  useEffect(() => {
    let vivo = true;
    void (async () => {
      try {
        const r = await api.get<CorreoParaIA[]>(`/api/casos/${casoId}/correos/para-ia`);
        if (vivo) setCorreos(r);
      } catch {
        // Es una ayuda para redactar, no un requisito: si falla, el modal sigue
        // sirviendo para escribir y mandar.
        if (vivo) setCorreos([]);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [casoId]);

  const marcados = useMemo(() => new Set(seleccion), [seleccion]);

  if (!correos || correos.length === 0) return null;

  const n = marcados.size;
  // El plural se calcula, no se asume: con un solo correo del caso el resumen
  // decía «los 1 van a la IA» (lo cazó Joe en cuanto lo prendió).
  const resumen =
    n === 0
      ? "ninguno va a la IA"
      : n === correos.length
        ? n === 1
          ? "va a la IA"
          : `los ${n} van a la IA`
        : `${n} de ${correos.length} ${n === 1 ? "va" : "van"} a la IA`;

  function alternar(id: number) {
    const proximos = new Set(marcados);
    if (proximos.has(id)) proximos.delete(id);
    else proximos.add(id);
    onCambio([...proximos]);
  }

  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => setAbierta((v) => !v)}
        className="text-xs text-zinc-400 transition hover:text-zinc-200
                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500"
      >
        {abierta ? "▾" : "▸"} Correos del caso ({correos.length}){" "}
        <span className={n > 0 ? "text-accent-400" : "text-zinc-500"}>· {resumen}</span>
      </button>

      {abierta && (
        <div className="mt-2">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-[11px] text-zinc-500">
              Lo que marques aquí lo <strong className="text-zinc-400">lee la IA</strong> al
              redactar. No se manda nada tal cual.
            </p>
            <button
              type="button"
              onClick={() => onCambio(n === correos.length ? [] : correos.map((c) => c.id))}
              className="shrink-0 text-[11px] text-accent-400 hover:text-accent-300"
            >
              {n === correos.length ? "Quitar todos" : "Marcar todos"}
            </button>
          </div>

          {/* En móvil se topa para no comerse la pantalla; en escritorio vive
              dentro de una columna que YA scrollea y un scroll adentro de otro
              es de lo peor que hay. */}
          <ul className="scroll-sutil max-h-72 space-y-1.5 overflow-y-auto lg:max-h-none lg:overflow-visible">
            {correos.map((c) => {
              const marcado = marcados.has(c.id);
              return (
                <li key={c.id}>
                  <label
                    className={
                      "flex cursor-pointer items-start gap-2 rounded border px-2 py-1.5 transition " +
                      (marcado
                        ? "border-accent-500/40 bg-accent-500/5"
                        : "border-ink-700 bg-ink-800/40 hover:border-ink-600")
                    }
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={marcado}
                      onChange={() => alternar(c.id)}
                    />
                    <span className="shrink-0 pt-0.5">
                      <ToggleTrack checked={marcado} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-baseline gap-x-1.5 text-[10px] text-zinc-500">
                        <span
                          className={
                            "rounded px-1 py-px " +
                            (c.direccion === "in"
                              ? "bg-sky-500/10 text-sky-300"
                              : "bg-ink-700 text-zinc-400")
                          }
                        >
                          {c.direccion === "in" ? "Recibido" : "Enviado"}
                        </span>
                        <span className="truncate text-zinc-400">{c.quien}</span>
                        <span>{c.fecha}</span>
                        {c.reenviado_de && (
                          <span className="rounded bg-ink-700 px-1 py-px text-zinc-400">
                            reenvío de {c.reenviado_de}
                          </span>
                        )}
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-zinc-200">
                        {c.asunto || "(sin asunto)"}
                      </span>
                      {c.adelanto && (
                        <span className="mt-0.5 block text-[11px] leading-snug text-zinc-400 line-clamp-2">
                          {c.adelanto}
                        </span>
                      )}
                      {/* Todos los archivos, por su nombre y partidos en
                          renglones: antes iban tres y un «+N», con la lista
                          completa en un tooltip que en el celular no existe. */}
                      {c.adjuntos.length > 0 && (
                        <span className="mt-0.5 flex items-start gap-1 text-[10px] text-zinc-500">
                          <IconoAdjunto className="mt-px h-3 w-3 shrink-0" />
                          <span className="min-w-0 break-all">{c.adjuntos.join(", ")}</span>
                        </span>
                      )}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
